import AdManager from '../ads/ad-manager.js';
import PianoHash from './piano-hash.js';
import SourcePoint from '../gdpr/sourcepoint.js';
import { GTM_DE } from '../../scripts-legacy/components/gtm/gtm-de';
import { adobeAnalytics } from '../adobe/adobe-analytics';
import ModalList from '../../scripts-legacy/components/modal-list';
import { slideShowDE } from '../../scripts-legacy/components/slideshow-de.js';
import { slideShowNL } from '../../scripts-legacy/components/slideshow-nl.js';
import StickyBanner from '../ads/sticky-banner.js';
import Cookies from 'js-cookie';
import YoastFAQ from '../yoast/yoast-faq.js';
import SHA256 from 'crypto-js/sha256';
import Hex from 'crypto-js/enc-hex';
import PianoSettings from './piano-settings';
import Bulletpoints from './premium-features/bulletpoints';
import GEA from '../gea/gea.js';
import Cxense from './cxense';
import DebugHelper from '../helpers/debug-helper';

class Piano {
	constructor() {
		this.logger = new DebugHelper('Piano');

		this.settings = {
			classes: {
				container: '.piano-main',
				accountContainer: '.piano-account',
				articleContainer: '.piano-article',
				accountTrigger: '.js-piano-profile-trigger',
				accountDropdown: '.piano-profile__dropdown',
				accountDropdownLoading: '.piano-profile__dropdown--loading',
				loginButton: '.piano-button__login',
				logoutButton: '.piano-button__logout',
				intLoginButton: '.piano-button__international',
				intLoginMainMenu: '.piano-international-login',
				registerButton: '.piano-button__register',
				accountButton: '.piano-button__profile',
				loader: '.piano__loader',
				loaderHidden: '.piano__loader--hidden',
				placeholder: '.sourcepoint__piano-placeholder',
				placeholderButton: '.sourcepoint__piano-placeholder button',
				paywallElement: '.piano-article__paywall',
			},
			gea_redirect_param: 'gea_redirect',
		};

		// Define TinyPass object
		this.tp = [];

		this.postMessageInterval = '';

		// This is a salt that is used to encrypt the user ID
		// This encrypted version of the user ID is used to identify the user in Adobe Analytics without exposing
		// any personal data. This encrypted user ID is never decrypted.
		this.encryptionPassPhrase =
			'fEbDTt5U59mBbvpX9IIdG4KQC0tWXonn7uPOz3uhHebsmO0A+KRjkw2+PMaUsRmF';

		// Set no-consent placeholder by default
		if (bi_theme.environment === 'de') {
			this.setPlaceholders(false);
		}
	}

	isActive() {
		if (typeof window.bi_piano !== 'undefined') {
			return true;
		}
		return false;
	}

	isAllowed() {
		// No consent is not required for NL
		if (bi_theme.environment === 'nl') {
			return true;
		}

		if (!SourcePoint.vendorHasGrant('piano')) {
			return false;
		}

		return true;
	}

	isSandbox() {
		return !!(
			typeof window.bi_piano.sandbox !== 'undefined' && window.bi_piano.sandbox
		);
	}

	maybeInit() {
		// If Piano is not active or not allowed, bail early
		if (!this.isActive() || !this.isAllowed()) {
			// Even when Piano is not active, we still need to load the scripts for ads
			this.toggleScriptsForPremiumUsers();

			return;
		}

		// Init Piano settings. Must be done before loading the SDK
		PianoSettings.init();

		// Load Piano SDK.
		this.loadSDK();

		// Load Cxense
		Cxense.maybeInit(this.tp);
	}

	loadSDK() {
		const url = window.bi_piano.sdk_url;

		// Check if the URL is not undefined
		if (typeof url === 'undefined' || url === null) {
			console.error('Piano SDK URL is undefined or null.');
			return;
		}

		// Check if the SDK is already loaded
		if (document.querySelector(`script[src="${url}"]`)) {
			return;
		}

		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.async = true;
		script.src = url;

		script.onload = () => {
			this.init();
		};

		script.onerror = () => {
			console.error('Piano SDK could not be loaded.');
		};

		const firstScript = document.getElementsByTagName('script')[0];
		firstScript.parentNode.insertBefore(script, firstScript);
	}

	init() {
		// Set window.tp variable (as preferred by Piano documentation)
		// See: https://docs.piano.io/track/implementing-piano/#menu7907
		this.tp = window.tp;

		// Bind event listeners
		this.bindEventListeners();

		// Setup account menu dropdown
		this.adjustAccountMenu();
		this.toggleAccountMenuItems();

		// Setup piano ID for user management
		this.setupPiano();

		// Maybe load account data
		this.maybeloadAccountData();

		// Maybe init Google Extended Access
		GEA.maybeInit();
	}

	setupPiano() {
		const self = this;

		// Set Application ID
		this.tp.push(['setAid', window.bi_piano.aid]);

		// Is application in sandbox?
		if (self.isSandbox()) {
			this.tp.push(['setSandbox', true]);

			// Enable for debugging purposes
			// this.tp.push( ["setDebug", true ] );
		}

		// Use Piano ID as user provider
		// see: https://docs.piano.io/piano-id-overview/#idinitialization
		this.tp.push(['setUsePianoIdUserProvider', true]);

		// Init Piano ID and hook it to the init callstack
		this.tp.push([
			'init',
			() => {
				// Init Experience
				// See: https://docs.piano.io/track/implementing-piano/#implementsinglepage
				self.tp.experience.init();

				// Init Piano ID
				self.tp.pianoId.init({
					containerSelector: self.getClassName('container'),
					stage: this.isNativeApp() ? 'app' : 'web',
					loggedIn(data) {
						self.userLoggedIn(data);
						self.getAdType().then(() => {
							Bulletpoints.init();
						});
					},
					loggedOut() {
						self.userLoggedOut();
					},
				});

				self.maybeResetPassword();
				self.toggleScriptsForPremiumUsers();
				self.removeEmailConfirmationLink();
				self.getSubscriptionId();
				self.userReferalId();
				PianoHash.init();

				// Remove verification code param from URL after checkout
				self.tp.push([
					'addHandler',
					'checkoutComplete',
					function (conversion) {
						const url = new URL(document.location);
						if (url.searchParams.has('verification_code') === true) {
							url.searchParams.delete('verification_code');
							window.history.replaceState(null, null, url);
						}

						// Add subscription data to the datalayer
						adobeAnalytics.sendCheckoutComplete({
							hasConverted: true,
							termId: conversion.termId,
							termConversionId: conversion.termConversionId,
						});
					},
				]);

				// Add referal ID cookie to piano register form when opened from the paywall
				// The default hook for opening the register form (see #253) doesnt work when opened
				// from the paywall
				self.tp.push([
					'addHandler',
					'checkoutStateChange',
					function (stateView) {
						if (stateView.stateName === 'auth') {
							self.sendReferalIdCookie('paywall-registration');
						}
					},
				]);

				// Add a param to the URL after a successful checkout
				self.tp.push([
					'addHandler',
					'checkoutClose',
					function (event) {
						if (event && event.state === 'checkoutCompleted') {
							let url = new URL(location.href);
							let UrlParams = new URLSearchParams(url.search);

							// Add custom param to the main URL for tracking purposes
							UrlParams.append('purchase', 'true');

							// Redirect to new url
							window.location.search = UrlParams;
							return false;
						}
					},
				]);
			},
		]);
	}

	setPlaceholders(has_consent) {
		const self = this;

		if (typeof window.bi_piano?.placeholder === 'undefined') {
			return;
		}

		if (!has_consent) {
			const loaderElements = jQuery(document).find(this.getClassName('loader'));
			const placeholder = jQuery(window.bi_piano.placeholder);

			jQuery.each(loaderElements, function () {
				// Show the placeholder if the element has the data-show-placeholder attribute
				if (jQuery(this).attr('data-show-placeholder')) {
					// Hide the loader
					jQuery(this).addClass(self.getClassName('loaderHidden', true));

					// Add the placeholder after the loader element
					jQuery(this).after(placeholder);
				}
			});

			return;
		}
	}

	userLoggedIn(userData) {
		this.logger.log('User logged in: ', userData);

		this.geaMaybeRedirect();
		this.adjustAccountMenu(userData);
		this.toggleAccountMenuItems();
		this.maybeloadAccountData();

		if (userData?.user) {
			this.maybeLoadPremiumContent(userData.user);
			this.fireDataLayerDimension(userData.user);
		}
	}

	userLoggedOut() {
		this.adjustAccountMenu();
		this.toggleAccountMenuItems();
		window.location.reload();
	}

	toggleAccountMenuItems() {
		if (this.isUserValid()) {
			jQuery('.piano-button--logged-out').hide();
			jQuery('.piano-button--logged-in').show();
		} else {
			jQuery('.piano-button--logged-in').hide();
			jQuery('.piano-button--logged-out').show();
		}
	}

	bindEventListeners() {
		const self = this;
		jQuery(document)
			.find(this.getClassName('loginButton'))
			.on('click', function () {
				self.StartLoginFlow();
			});
		jQuery(document)
			.find(this.getClassName('logoutButton'))
			.on('click', function () {
				self.StartLogoutFlow();
			});
		jQuery(document)
			.find(this.getClassName('registerButton'))
			.on('click', function () {
				self.startRegisterFlow();
			});
		jQuery(document)
			.find(this.getClassName('accountTrigger'))
			.on('click', function () {
				self.adjustAccountDropdownState(false);
			});
		jQuery('.toggle-auto-renew').each(function () {
			jQuery(this).on('click', function () {
				self.updateAutoRenew(this);
			});
		});

		window.addEventListener(
			'message',
			(event) => {
				self.resetPostMessageInterval(event);
			},
			false,
		);
	}

	adjustAccountDropdownState(loading) {
		const $accountDropdown = jQuery(document).find(
				this.getClassName('accountDropdown'),
			),
			accountDropdownLoadingClass = this.getClassName(
				'accountDropdownLoading',
				true,
			);

		if (loading) {
			$accountDropdown.addClass(accountDropdownLoadingClass);
		} else {
			$accountDropdown.removeClass(accountDropdownLoadingClass);
		}
	}

	openAccountDropdown() {
		jQuery(this.getClassName('accountTrigger')).attr('aria-expanded', true);
		jQuery(this.getClassName('accountDropdown')).attr('aria-hidden', false);
	}

	closeAccountDropdown() {
		jQuery(this.getClassName('accountTrigger')).attr('aria-expanded', false);
		jQuery(this.getClassName('accountDropdown')).attr('aria-hidden', true);
	}

	resetAccountDropdown() {
		this.adjustAccountDropdownState(false);
		this.closeAccountDropdown();
	}

	adjustAccountMenu(userData) {
		jQuery(this.getClassName('accountTrigger')).attr('aria-hidden', false);

		if (this.isUserValid()) {
			this.toggleElement(this.getClassName('loginButton'), false);
			this.toggleElement(this.getClassName('accountButton'), true);
			this.toggleElement(this.getClassName('logoutButton'), true);
			this.maybeReplaceInternationalLinks(userData);
		} else {
			this.toggleElement(this.getClassName('loginButton'), true);
			this.toggleElement(this.getClassName('accountButton'), false);
			this.toggleElement(this.getClassName('logoutButton'), false);
			this.toggleElement(this.getClassName('intLoginButton'), false);
		}

		this.adjustAccountDropdownState(false);
	}

	maybeReplaceInternationalLinks(userData) {
		const self = this;
		if (typeof userData === 'undefined') {
			return;
		}

		const premiumResourceIds = this.getPremiumResourceIds();
		if (!premiumResourceIds) {
			return;
		}

		if (typeof this.tp.api === 'undefined') {
			return;
		}

		// Check if the logged in Piano user has premium access
		this.tp.api.callApi('/access/list', { uid: userData.uid }, function (data) {
			const access = data.data;
			let internationalLink = '';

			let hasAccess = false;
			for (let i = 0; i < access.length; i++) {
				const accessGranted = access[i].granted;
				const resourceID = access[i].resource.rid;
				if (accessGranted && premiumResourceIds.includes(resourceID)) {
					hasAccess = true;
				}
			}

			// Create separate links for users with and without premium access
			internationalLink = self.generateInternationalLoginLink(
				userData,
				hasAccess,
			);

			// Adjust the international link in the account menu
			jQuery(self.getClassName('intLoginButton')).attr(
				'href',
				internationalLink,
			);
			self.toggleElement(self.getClassName('intLoginButton'), true);

			// Adjust the international link in the main menu
			jQuery(self.getClassName('intLoginMainMenu')).attr(
				'href',
				internationalLink,
			);
		});
	}

	generateInternationalLoginLink(userData, hasAccess) {
		const source =
			typeof window.bi_piano.source !== 'undefined'
				? window.bi_piano.source
				: 'binl';
		if (typeof userData.token === 'undefined' || !hasAccess) {
			return `https://www.businessinsider.com/?IR=T&source=${source}`;
		}

		return `https://www.businessinsider.com/?IR=T&token=${userData.token}&source=${source}`;
	}

	isUserValid() {
		if (this.tp.user !== undefined) {
			return this.tp.user.isUserValid();
		}
		return false;
	}

	StartLoginFlow() {
		const self = this;
		this.adjustAccountDropdownState(true);
		this.tp.pianoId.show({
			screen: 'login',
			stage: this.isNativeApp() ? 'app' : 'web',
			loginDisplayed() {
				self.resetAccountDropdown();
			},
		});
	}

	StartLogoutFlow() {
		this.adjustAccountDropdownState(true);
		this.tp.pianoId.logout();
	}

	startRegisterFlow() {
		const self = this;
		this.adjustAccountDropdownState(true);
		this.tp.pianoId.show({
			screen: 'register',
			stage: this.isNativeApp() ? 'app' : 'web',
			registerDisplayed() {
				self.resetAccountDropdown();
				self.sendReferalIdCookie();
			},
		});
	}

	isSingleArticle() {
		return typeof window.bi_piano.post_id !== 'undefined' &&
			window.bi_piano.post_id &&
			!isNaN(window.bi_piano.post_id)
			? true
			: false;
	}

	isPremiumArticle() {
		return typeof window.bi_piano.is_premium !== 'undefined' &&
			window.bi_piano.is_premium
			? true
			: false;
	}

	getPremiumResourceIds() {
		return typeof window.bi_piano.premium_resource_ids !== 'undefined' &&
			window.bi_piano.premium_resource_ids
			? window.bi_piano.premium_resource_ids
			: false;
	}

	getAdFreeTermIds() {
		return typeof window.bi_piano.premium_no_ads !== 'undefined' &&
			window.bi_piano.premium_no_ads
			? window.bi_piano.premium_no_ads
			: false;
	}

	isRegistrationOnly() {
		return typeof window.bi_piano.is_registration_only !== 'undefined' &&
			window.bi_piano.is_registration_only
			? true
			: false;
	}

	maybeLoadPremiumContent(userData) {
		if (
			!this.isSingleArticle() ||
			!this.isPremiumArticle() ||
			!this.isUserValid()
		) {
			return;
		}

		const premiumResourceIds = this.getPremiumResourceIds();
		if (!premiumResourceIds) {
			return;
		}

		this.loadPremiumContent(
			window.bi_piano.post_id,
			userData.uid,
			this.isRegistrationOnly(),
		);
	}

	maybeLoadPremiumContentApp(userData) {
		if (
			!this.isSingleArticle() ||
			!this.isPremiumArticle() ||
			window.bi_piano.post_id === 'undefined'
		) {
			return;
		}

		const premiumResourceIds = this.getPremiumResourceIds();
		if (!premiumResourceIds) {
			return;
		}

		this.loadPremiumContent(
			window.bi_piano.post_id,
			userData.uid,
			this.isRegistrationOnly(),
		);
	}

	isNativeApp() {
		let isNativeApp = false;
		if (window.bi_native_app !== undefined) {
			isNativeApp = window.bi_native_app.is_native_app ? true : false;
		}
		return isNativeApp;
	}

	loadPremiumContent(postId, userId, isRegistrationOnly) {
		const self = this;
		const isNativeApp = this.isNativeApp();

		jQuery.ajax({
			type: 'GET',
			url: '/wp-json/bi/piano/get-post-content/',
			dataType: 'json',
			data: {
				postId,
				userId,
				isRegistrationOnly,
				isNativeApp,
			},
			success(resp) {
				if (resp.content) {
					self.maybeHideDefaultSharing();
					self.insertPremiumContent(resp.content);

					// Load the premium Bulletpoints
					if (window.bi_piano?.premium_features?.bulletpoints === true) {
						self.getAdType().then(() => {
							Bulletpoints.init();
						});
					}

					SourcePoint.resetElements();
					self.activateSlideshows();
					YoastFAQ.addAccordion();
					document
						.querySelectorAll('[data-module=socials]')
						.forEach((element) => {
							window.app.instantiate(element);
						});
				}
			},
			error() {
				self.resetPaywallContent(true);
			},
		});
	}

	loadPremiumContentAfterAdView(postId) {
		const self = this;
		const isNativeApp = this.isNativeApp();

		jQuery.ajax({
			type: 'GET',
			url: '/wp-json/bi/piano/get-post-content-after-ad-view/',
			dataType: 'json',
			data: {
				postId,
				isNativeApp,
			},
			success(resp) {
				if (resp.content) {
					self.maybeHideDefaultSharing();
					self.insertPremiumContent(resp.content);

					// Load the premium Bulletpoints
					if (window.bi_piano?.premium_features?.bulletpoints === true) {
						self.getAdType().then(() => {
							Bulletpoints.init();
						});
					}

					SourcePoint.resetElements();
					self.activateSlideshows();
					YoastFAQ.addAccordion();
					document
						.querySelectorAll('[data-module=socials]')
						.forEach((element) => {
							window.app.instantiate(element);
						});
				}
			},
			error() {
				self.resetPaywallContent(true);
			},
		});
	}

	resetPaywallContent(hide) {
		const $articleContainer = jQuery(this.getClassName('articleContainer')),
			loaderClass = this.getClassName('loader'),
			loaderClassHidden = this.getClassName('loaderHidden', true);

		if (hide) {
			$articleContainer.children().not(loaderClass).show();
			$articleContainer.find(loaderClass).addClass(loaderClassHidden);
		} else {
			$articleContainer.children().not(loaderClass).hide();
			$articleContainer.find(loaderClass).removeClass(loaderClassHidden);
		}
	}

	insertPremiumContent(content) {
		jQuery(this.getClassName('articleContainer')).replaceWith(jQuery(content));
	}

	activateSlideshows() {
		if (
			typeof ModalList !== 'undefined' &&
			typeof bi_theme.environment !== 'undefined'
		) {
			if (bi_theme.environment === 'de') {
				ModalList.init(slideShowDE);
			}

			if (bi_theme.environment === 'nl') {
				ModalList.init(slideShowNL);
			}
		}
	}

	maybeloadAccountData() {
		const accountContainerClass = this.getClassName('accountContainer'),
			$accountContainer = jQuery(document).find(accountContainerClass);

		if ($accountContainer && $accountContainer.length > 0) {
			if (this.isUserValid()) {
				this.loadAccountData(accountContainerClass);
			} else {
				this.StartLoginFlow();
			}
		}
	}

	loadAccountData(accountContainerClass) {
		this.tp.myaccount.show({
			displayMode: 'inline',
			containerSelector: accountContainerClass,
		});
	}

	maybeResetPassword() {
		const resetTokenMatch = this.getPasswordResetToken();
		if (!this.isUserValid() && resetTokenMatch) {
			const self = this;
			this.tp.pianoId.show({
				resetPasswordToken: resetTokenMatch[1],
				loggedIn(data) {
					self.userLoggedIn(data);
				},
			});
		}
	}

	getPasswordResetToken() {
		return window.location.search.match(/reset_token=([A-Za-z0-9]+)/);
	}

	setAdTypeCookie(type) {
		Cookies.set('bi_ad_type', type, { expires: 7 });
	}

	getAdTypeCookie() {
		const cookie = Cookies.get('bi_ad_type');
		if (typeof cookie === 'undefined') {
			return false;
		}
		return cookie;
	}

	getAdType() {
		return new Promise((resolve) => {
			const self = this;

			if (!this.isAllowed()) {
				resolve('full_ads');
			}

			if (
				typeof bi_ad_settings === 'undefined' ||
				typeof bi_theme.environment === 'undefined'
			) {
				resolve('full_ads');
			}

			const adTypeCookie = self.getAdTypeCookie();
			if (adTypeCookie === 'no_content_ads') {
				resolve('no_content_ads');
			}

			if (adTypeCookie === 'no_ads') {
				resolve('no_ads');
			}

			if (typeof this.tp.api === 'undefined') {
				resolve('full_ads');
			}

			let adType = 'full_ads';
			self.tp.api.callApi('/conversion/list', {}, function (data) {
				self.logger.log('Getting conversion data from Piano: ', data);

				const premium_no_ads_list = self.getAdFreeTermIds();
				if (
					typeof data.conversions !== 'undefined' &&
					data.conversions.length > 0
				) {
					for (let i = 0; i < data.conversions.length; i++) {
						let dataGrantedDefined =
							typeof data.conversions[i].user_access !== 'undefined';
						if (dataGrantedDefined) {
							dataGrantedDefined =
								typeof data.conversions[i].user_access.granted !== 'undefined';
						}

						let dataTermDefined =
							typeof data.conversions[i].term !== 'undefined';
						if (dataTermDefined) {
							dataTermDefined =
								typeof data.conversions[i].term.term_id !== 'undefined';
						}

						// Check if adType is not already no_ads or no_content_ads, so we don't downgrade the adType for the user
						if (
							dataGrantedDefined &&
							data.conversions[i].user_access.granted === false &&
							adType !== 'no_ads' &&
							adType !== 'no_content_ads'
						) {
							adType = 'full_ads';
						}

						// Set access type to no_ads if user has an ad free subscription
						if (
							dataGrantedDefined &&
							data.conversions[i].user_access.granted === true &&
							dataTermDefined &&
							premium_no_ads_list.includes(data.conversions[i].term.term_id)
						) {
							adType = 'no_ads';
						}

						// Set access type to no_content_ads if user has a regular subscription
						// Check if adType is not already no_ads, so we don't downgrade the adType for the user
						if (
							dataGrantedDefined &&
							data.conversions[i].user_access.granted === true &&
							adType !== 'no_ads'
						) {
							adType = 'no_content_ads';
						}

						if (data.conversions[i].user_access.granted === true) {
							const conversionData = {
								termId: data.conversions[i].term.term_id,
								timestamp: data.conversions[i].create_date,
							};

							self.logger.log(
								'Sending conversion data to data layer: ',
								conversionData,
							);
							adobeAnalytics.sendConversionData(conversionData);
						}
					}
				}

				if (adType === 'no_ads' || adType === 'no_content_ads') {
					self.setAdTypeCookie(adType);
				}

				resolve(adType);
			});
		});
	}

	/**
	 * If current user is premium, toggle:
	 * ads permission
	 * sticky banner
	 * subscribe button
	 */
	toggleScriptsForPremiumUsers() {
		if (
			typeof bi_ad_settings === 'undefined' ||
			typeof bi_theme.environment === 'undefined'
		) {
			return;
		}

		// Handle the scripts / ads based on the adType cookie for DE
		if (bi_theme.environment === 'de') {
			this.getAdType().then((adType) => {
				if (adType === 'no_ads' || adType === 'no_content_ads') {
					// Set ad permission to false
					bi_ad_settings.ads_permission = false;
					bi_ad_settings.is_sticky_banner = false;
					AdManager.changeInArticleOutbrainAds();
					AdManager.changeInArticleApesterAds();
					AdManager.removeForbiddenAdScripts();
				} else if (adType === 'full_ads' || typeof adType === 'undefined') {
					// Set ad permission to true
					bi_ad_settings.ads_permission = true;
				}
			});

			SourcePoint.maybeLoadVendors();
			StickyBanner.init();
		}

		// Handle the scripts / ads based on a call to Piano for NL
		if (
			bi_theme.environment === 'nl' &&
			typeof this.tp?.api?.callApi === 'function'
		) {
			this.tp.api.callApi('/access/list', {}, function (data) {
				if (typeof data.count !== 'undefined' && data.count > 0) {
					bi_ad_settings.ads_permission = false;
					jQuery('.header-nav__item--subscribe-button').remove();
				} else {
					bi_ad_settings.ads_permission = true;
				}
			});
		} else {
			bi_ad_settings.ads_permission = true;
		}
	}

	toggleElement(elementClass, show) {
		if (show) {
			jQuery(elementClass).removeClass(
				elementClass.replace('.', '') + '--hidden',
			);
		} else {
			jQuery(elementClass).addClass(elementClass.replace('.', '') + '--hidden');
		}
	}

	getClassName(elementName, removePeriod) {
		if (typeof this.settings.classes[elementName] !== 'undefined') {
			if (removePeriod) {
				return this.settings.classes[elementName].replace('.', '');
			}
			return this.settings.classes[elementName];
		}
		return false;
	}

	toggleLoader(show) {
		if (show) {
			this.toggleElement(this.getClassName('loader'), true);
		} else {
			this.toggleElement(this.getClassName('loader'), false);
		}
	}

	openModal(screen) {
		const allowedTypes = ['login', 'restore', 'register'];

		if (typeof screen === 'string' && allowedTypes.includes(screen)) {
			this.tp.pianoId.show({
				screen,
			});
		}
	}

	maybeHideDefaultSharing() {
		if (!window.bi_piano?.premium_features?.sharing) {
			return;
		}

		const $shareWrapper = jQuery(document).find('.social-wrap');
		const $shareList = jQuery(document).find('.social-share');
		$shareWrapper.remove();
		$shareList.remove();
	}

	fireDataLayerDimension(userData) {
		if (typeof GTM_DE !== 'undefined') {
			// Fire custom dimension
			GTM_DE.sendSubscriptionState({
				isSubscribed: true,
			});
		}
		if (
			bi_theme.environment === 'de' &&
			typeof adobeAnalytics !== 'undefined'
		) {
			adobeAnalytics.sendSubscriptionState({
				isSubscribed: true,
			});
			adobeAnalytics.sendUniqueUserId({
				customerId: this.generateCustomerId(userData.uid),
			});
		}
	}

	generateCustomerId(userId) {
		let formattedUserId = this.hashString(userId);
		return 'bi_' + formattedUserId;
	}

	hashString(input) {
		const saltedInput = input + this.encryptionPassPhrase;
		const hash = SHA256(saltedInput);
		return hash.toString(Hex);
	}

	/*
	Temp fix for problems with piano picking up the correct conversion URL
	 */
	removeEmailConfirmationLink() {
		// A small timeout is need to be sure that Piano has time to pickup the verification code
		setTimeout(function () {
			const url = new URL(document.location);
			if (url.searchParams.has('email_confirmation_url') === true) {
				const confirmationUrl = new URL(
					url.searchParams.get('email_confirmation_url'),
				);
				if (confirmationUrl.searchParams.has('verification_code') === true) {
					url.searchParams.append(
						'verification_code',
						confirmationUrl.searchParams.get('verification_code'),
					);
				}
				url.searchParams.delete('email_confirmation_url');
				window.history.replaceState(null, null, url);
			}
		}, 1500);
	}

	userReferalId() {
		const url = new URL(window.location.href);
		const params = new URLSearchParams(url.search);
		if (params.has('k_id') === true) {
			Cookies.set('k_id', params.get('k_id'));
		}
	}

	sendReferalIdCookie(type) {
		let iframe;
		const parentIframe = jQuery('.tp-iframe-wrapper iframe');

		// Two different types of iframes are used. One for the register button in the account menu
		// and one for the register form that is opened from the paywall. Both need different
		// targets.
		if (type === 'paywall-registration') {
			iframe = parentIframe[0].contentWindow.frames[2];
		} else {
			iframe = parentIframe[0].contentWindow;
		}

		// A small interval is needed to make sure the postMessage to the iframe is send multiple times
		// until a confirmation from the iframe is received. When the iframe is loaded it's sends a
		// postMessage back to clear the interval
		this.postMessageInterval = setInterval(() => {
			this.sendPostMessage(iframe);
		}, 500);
	}

	sendPostMessage(iframe) {
		const cookie = Cookies.get('k_id');
		if (typeof cookie === 'undefined') {
			return;
		}
		const data = { hook: 'kajomi_set_id', k_id: cookie };

		// Send data to the piano iframe with postMessage
		iframe.postMessage(data, window.bi_piano.pianoid_iframe_url);
	}

	resetPostMessageInterval(event) {
		if (
			event.origin !== 'https://sandbox.tinypass.com' &&
			event.origin !== 'https://id.tinypass.com'
		) {
			return;
		}
		if (event.data.hook !== 'kajomi_set_id_confirmed') {
			return;
		}
		clearInterval(this.postMessageInterval);
	}

	getSubscriptionId() {
		const url = new URL(document.location);
		let subscriptionId = '';
		let autoRenew = false;

		if (url.searchParams.has('subscription_start_auto_renew') === true) {
			subscriptionId = url.searchParams.get('subscription_start_auto_renew');
			autoRenew = true;
		}

		if (url.searchParams.has('subscription_stop_auto_renew') === true) {
			subscriptionId = url.searchParams.get('subscription_stop_auto_renew');
			autoRenew = false;
		}

		return [subscriptionId, autoRenew];
	}

	updateAutoRenew(currentElement) {
		jQuery(currentElement)
			.parent()
			.prepend(
				'<div class="piano__loader-spinner"><div></div><div></div><div></div><div></div></div>',
			);
		const params = this.getSubscriptionId();
		const subscriptionId = params[0];
		const autoRenew = params[1];
		const userId = this.tp.pianoId.getUser().uid;

		// Update auto_renew
		jQuery.ajax({
			type: 'GET',
			url: '/wp-json/bi/piano/toggle-auto-renew/',
			dataType: 'json',
			data: {
				subscriptionId,
				autoRenew,
				userId,
			},
			success(resp) {
				const url = new URL(resp);
				if (autoRenew === true) {
					url.searchParams.append(
						'subscription_stop_auto_renew',
						subscriptionId,
					);
				}
				if (autoRenew === false) {
					url.searchParams.append(
						'subscription_start_auto_renew',
						subscriptionId,
					);
				}

				window.location.replace(url.toString());
			},
			error(resp) {
				jQuery(currentElement)
					.parent()
					.parent()
					.prepend(
						'<div class="piano-subscription-button--error" aria-live="polite" aria-describedby"piano-error"><h2 id="piano-error">Subscription update failed</h2>' +
							resp.responseJSON.data.custom_error_message +
							'</div>',
					);
				jQuery('.piano__loader-spinner').remove();
			},
		});
	}

	/**
	 * Get the Piano SWG URL
	 * @param redirect_uri Must be an encoded URL
	 */
	async GeaGetSwgUrl(redirect_uri) {
		this.logger.log(`Get Piano SWG URL: ${redirect_uri}`);

		const rest_url = window.bi_piano.rest_get_swg_url;

		if (typeof rest_url === 'undefined') {
			throw new Error('LL Piano: Get Piano SWG URL: No valid rest URL defined');
		}

		if (!this.geaUrlIsEncoded(redirect_uri)) {
			throw new Error(
				'LL Piano: Get Piano SWG URL: Redirect URI should be encoded!',
			);
		}

		try {
			const fetch_url = `${rest_url}?internal_redirect_uri=${redirect_uri}`;
			this.logger.log(`Get Piano SWG URL from: ${fetch_url}`);

			const response = await fetch(fetch_url);
			this.logger.log('Get Piano SWG URL response: ', response);

			if (!response.ok) {
				this.logger.log(`HTTP error! Status: ${response.status}`);
			}

			const data = await response.json();
			this.logger.log('Get Piano SWG JSON response: ', data);

			if (!data.uri) {
				throw new Error(`No valid URI found in response`);
			}

			return data.uri;
		} catch (error) {
			console.error(`Error occurred while fetching Piano SWG URL: ${error}`);
			return null;
		}
	}

	geaUrlIsEncoded(uri) {
		return uri !== decodeURIComponent(uri);
	}

	/*
	 * Get the users data object
	 * @see: https://docs.piano.io/api?endpoint=post~2F~2Fanon~2Fuser~2Fget
	 */
	getUserData() {
		const self = this;
		return new Promise((resolve, reject) => {
			if (typeof this.tp.api === 'undefined') {
				reject('No Piano API found');
			}

			this.tp.api.callApi('/anon/user/get', {}, function (userData) {
				self.logger.log(`Get user data: `, userData);
				if (userData.user === 'undefined') {
					reject('No user data found');
				} else {
					resolve(userData.user);
				}
			});
		});
	}

	/*
	 * Get the user access rights
	 * @see: https://docs.piano.io/api?endpoint=get~2F~2Faccess~2Flist
	 */
	getUserAccess() {
		const self = this;
		return new Promise((resolve, reject) => {
			if (typeof this.tp.api === 'undefined') {
				reject('No Piano API found');
			}

			const premiumResourceIds = this.getPremiumResourceIds();
			this.tp.api.callApi('/access/list', {}, function (accessData) {
				self.logger.log('Get user access data ', accessData);

				if (typeof premiumResourceIds === 'undefined') {
					reject('No premium types found');
				}

				if (typeof accessData.count === 'undefined' || accessData.count === 0) {
					resolve(accessData.data);
				}

				for (let i = 0; i < accessData.data.length; i++) {
					const accessGranted = accessData.data[i].granted;
					const resourceID = accessData.data[i].resource.rid;
					if (accessGranted && premiumResourceIds.includes(resourceID)) {
						resolve(accessData.data[i]);
					}
				}
			});
		});
	}

	/**
	 * Return GEA user state formatted as required by the GEA docs
	 * This method can be used to get an initial user state or to update publisherEntitlementPromise method of GEA
	 * @see https://developers.google.com/news/subscribe/extended-access/integration-steps/web-implementation?hl=en&feed=client-side#creating-the-users-entitlement-state
	 * @see https://developers.google.com/news/subscribe/extended-access/reference/user-state-object
	 */
	async geaGetUserState() {
		this.logger.log('Get user state');

		let userState = {
			granted: false,
		};

		try {
			const results = await Promise.all([
				this.getUserData(),
				this.getUserAccess(),
			]);

			this.logger.log('Return user state promises resolved', results);

			// Validate if the user has a registration date
			if (
				typeof results[0] !== 'undefined' &&
				typeof results[0].create_date !== 'undefined' &&
				typeof results[0].uid !== 'undefined'
			) {
				userState.id = results[0].uid;
				userState.registrationTimestamp = results[0].create_date;
			}

			// Validate if the user has a subscription date
			if (
				typeof results[1] !== 'undefined' &&
				typeof results[1].start_date !== 'undefined' &&
				typeof results[1].access_id !== 'undefined'
			) {
				userState.subscriptionTimestamp = results[1].start_date;
				userState.granted = true;
				userState.grantReason = 'SUBSCRIBER';
			}

			this.logger.log('Return user state: ', userState);

			return userState;
		} catch (error) {
			this.logger.log(`An error occurred while fetching user state: ${error}`);
		}

		return userState;
	}

	/**
	 * This will redirect to a page that opens the Piano login popup
	 * The actual URL (with GEA params) will be kept after login
	 */
	geaHandleLogin(actual_url) {
		this.logger.log('Login to Piano');

		// Redirect to a login page for existing users to login and keep the GEA params
		window.location = this.geaGetLoginUrl(actual_url);
	}

	/**
	 * Will remove the loader / premium content and show the paywall
	 */
	geaShowPaywall() {
		this.logger.log('Show paywall');
		this.resetPaywallContent(true);

		// Scroll to paywall
		const paywall = document.querySelector(
			this.settings.classes.paywallElement,
		);
		if (paywall) {
			paywall.scrollIntoView({ behavior: 'smooth' });
		}
	}

	/*
	 * Unlock the article (will remove paywall and load content)
	 * This works for all logged in registered users, with or without a subscription
	 */
	geaUnlockArticle() {
		this.logger.log('Unlock article');

		this.resetPaywallContent();

		if (!GEA.isValidUrl()) {
			this.logger.log('Unlock article: URL params not valid');
			this.resetPaywallContent(true);
			return;
		}

		if (!this.isPremiumArticle()) {
			this.logger.log('Unlock article: No premium article');
			this.resetPaywallContent(true);
			return;
		}

		if (!this.isUserValid()) {
			this.logger.log('Unlock article: User is not valid');
			this.resetPaywallContent(true);
			return;
		}

		this.loadPremiumContent(
			window.bi_piano.post_id,
			this.tp.pianoId.getUser().uid,
			true,
		);
	}

	/**
	 * After Piano login with GEA, redirect to the original GEA URL
	 */
	geaMaybeRedirect() {
		// Check if a specific URL param is set
		const url = new URL(document.location);

		this.logger.log('Maybe redirect after login');

		if (url.searchParams.has(this.settings.gea_redirect_param) === true) {
			this.logger.log(
				'Maybe redirect after login: Redirect to:',
				url.searchParams.get(this.settings.gea_redirect_param),
			);
			window.location = url.searchParams.get(this.settings.gea_redirect_param);
		}
	}

	geaGetLoginUrl(url) {
		this.logger.log('Get login URL');

		// Decode URL first to make sure it's not encoded twice
		url = decodeURIComponent(url);

		// Encode URL again
		url = encodeURIComponent(url);

		// Get the url without all params
		const urlWithoutParams = window.location.href.split('?')[0];
		const loginUrl = `${urlWithoutParams}?${this.settings.gea_redirect_param}=${url}#bi-premium-login`;

		this.logger.log(`Get login URL: ${loginUrl}`);

		// Return the login URL with the redirect param and has to force a Piano login modal
		return loginUrl;
	}
}

const instance = new Piano();
export default instance;
